import { useSyncExternalStore } from "react";
import { Loader2, LocateFixed } from "lucide-react";
import {
  getGeoStatus,
  requestUserCoords,
  subscribeUserCoords,
  type GeoStatus,
  type UserCoords,
} from "@/lib/user-location";

/**
 * "Use my location", shared by the hero search and the results-page search bar.
 *
 * Extracted rather than copied because the denial handling is the part that is
 * easy to get subtly wrong, and wrong in a way nobody notices: a browser
 * geolocation denial is STICKY and cannot be re-prompted, so the button must
 * stay visible and explain where the setting lives instead of vanishing or
 * silently doing nothing. Two copies of that rule would drift.
 *
 * Subscribes to the store itself so the spinner re-renders locally instead of
 * re-rendering whichever large form is hosting it.
 */
export function UseMyLocationButton({
  onLocated,
  className = "",
}: {
  /** Called only on success. Denial and unsupported resolve to no-ops. */
  onLocated: (coords: UserCoords) => void;
  className?: string;
}) {
  const status: GeoStatus = useSyncExternalStore(
    subscribeUserCoords,
    getGeoStatus,
    () => "idle" as GeoStatus,
  );
  const locating = status === "locating";

  return (
    <button
      type="button"
      // Not a submit: this button is inside a <form>, and without the explicit
      // type it would submit the form on click in every browser.
      onClick={async () => {
        const coords = await requestUserCoords();
        if (coords) onLocated(coords);
      }}
      disabled={locating || status === "unsupported"}
      title={
        status === "denied"
          ? "Location is blocked for this site — enable it in your browser's site settings"
          : "Use my location"
      }
      aria-label="Use my location"
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-primary disabled:opacity-50 cursor-pointer ${className}`}
    >
      {locating
        ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        : <LocateFixed className="h-4 w-4" aria-hidden />}
    </button>
  );
}
